import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { EvMainTitle, EvSection, EvSpinner } from '@evinced-private/ui-common';

import { ProductCard } from '../../components/product-card/ProductCard';
import { IProductCardData, ProductCardsData } from '../../consts/products-consts';
import {
	getProductToRedirectFromUrl,
	REDIRECT_TO_PRODUCT_VALUE
} from '../../helpers/InvitationTokenHelper';
import { openInNewTab } from '../../helpers/OpenLinksHelper';
import {
	getFlowAnalyzerForMobilePath,
	getMobileSDKPath,
	getSiteScannerPath
} from '../../helpers/RoutesHelper';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { Logger } from '../../services/Logger';
import { getProductPlanDisplayName } from '../../services/ProductPlansService';
import {
	getAllProductsExpirationDetails,
	getUserTenant,
	TenantExpirationDetails
} from '../../services/TenantsService';
import { ProductType } from '../../types/Tenant';

import { getPlatformCardData } from './helpers/PlatformCardHelper';

import './WelcomePage.scss';

type IPrimaryButtonMessageData = {
	[product in ProductType]: string;
};

export const WelcomePage: FC = () => {
	const history = useHistory();
	const { configuration } = useConfiguration();
	const [isLoading, setLoading] = useState(true);
	const [productExpirationDetails, setProductExpirationDetails] =
		useState<Map<ProductType, TenantExpirationDetails>>();
	const [primaryButtonMessageData, setPrimaryButtonMessageData] =
		useState<IPrimaryButtonMessageData>();

	useEffect(() => {
		getUserTenant().then((tenant) => {
			const mfaProduct = tenant.products?.find(
				({ type }) => type === ProductType.MOBILE_FLOW_ANALYZER
			);
			if (!mfaProduct?.planId) {
				return;
			}
			getProductPlanDisplayName(mfaProduct?.planId).then((mfaPlan) => {
				setPrimaryButtonMessageData((prevData) => {
					return {
						...prevData,
						[ProductType.MOBILE_FLOW_ANALYZER]: mfaPlan.toLowerCase().includes('community')
							? 'Community edition'
							: ''
					};
				});
			});
		});
	}, []);

	// returns true when the user is leaving the hub, so the page keeps showing the spinner
	const redirectIfNeeded = useCallback((): boolean => {
		const redirectionUrlParam = getProductToRedirectFromUrl();
		if (redirectionUrlParam === REDIRECT_TO_PRODUCT_VALUE.MOBILE_SDK) {
			history.push(getMobileSDKPath());
		} else if (redirectionUrlParam === REDIRECT_TO_PRODUCT_VALUE.MOBILE_FLOW_ANALYZER) {
			history.push(getFlowAnalyzerForMobilePath());
		} else if (redirectionUrlParam === REDIRECT_TO_PRODUCT_VALUE.SITE_SCANNER) {
			// Site Scanner is another origin - leave the hub in the same tab.
			// replace, so this redirect-only page isn't kept in history
			window.location.replace(getSiteScannerPath());
			return true;
		}
		return false;
	}, [history]);

	const checkForTokenValidity = useCallback(async (): Promise<void> => {
		setLoading(true);
		try {
			const productExpirationDetails = await getAllProductsExpirationDetails();
			setProductExpirationDetails(productExpirationDetails);
		} catch (err) {
			Logger.error('Error getting product activation links', err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (redirectIfNeeded()) {
			return;
		}
		checkForTokenValidity();
	}, [redirectIfNeeded, checkForTokenValidity]);

	const productsList = useMemo(() => {
		const products = ProductCardsData.map((product: IProductCardData) => {
			let fullProduct: IProductCardData = { ...product };
			fullProduct.expirationDetails = productExpirationDetails?.get(fullProduct.productType);
			// each product that needs to be redirected using the history object and has a path,
			// the goToProductButtonOnClick should be handled here.
			if (!fullProduct.goToProductButtonOnClick && fullProduct.path) {
				fullProduct = {
					...fullProduct,
					goToProductButtonOnClick: () => history.push(fullProduct.path)
				};
			}

			fullProduct.primaryButtonMessage = primaryButtonMessageData?.[fullProduct.productType];
			return fullProduct;
		});

		const platformCardData = getPlatformCardData(configuration, productExpirationDetails);
		if (!platformCardData) {
			return products;
		}

		const platformCard: IProductCardData = {
			productType: ProductType.PLATFORM,
			cardTitle: 'Platform',
			contentText: platformCardData.description,
			goToProductButtonText: 'Get started',
			goToProductButtonOnClick: platformCardData.redirectionUrl
				? () => openInNewTab(platformCardData.redirectionUrl)
				: undefined,
			expirationDetails: platformCardData.expirationDetails
		};

		return [...products, platformCard];
	}, [configuration, history, primaryButtonMessageData, productExpirationDetails]);

	if (isLoading) {
		return (
			<div className="welcome-page">
				<EvSpinner />
			</div>
		);
	}

	return (
		<div className="welcome-page">
			<div className="welcome-page-content">
				<EvMainTitle className="main-welcome-title">Welcome to Evinced Product Hub!</EvMainTitle>
				<div className="welcome-sub-title">
					Evinced provides a comprehensive digital accessibility product suite for software
					development teams.
				</div>

				<EvSection className="cards-section">
					{productsList.map((product: IProductCardData) => (
						<ProductCard key={product.productType} {...product} className="welcome-product-card" />
					))}
				</EvSection>
			</div>
		</div>
	);
};
